// How a shelf orders its books. The signage app and the highlight carousel
// both sort through here, because the carousel steps through a shelf in the
// order the shelf drew it - two orderings would make "next" jump around.

export const DATE_READ = 'dateRead';
export const DATE_ADDED = 'dateAdded';
export const SHELF_DATE = 'shelfDate';
export const PUBLISHED = 'publishedYear';
export const RATING = 'rating';
export const AUTHOR = 'author';
export const TITLE = 'title';
export const DESCENDING = 'desc';

// Which date each shelf is actually about.
//
// A single date field is the wrong field on half a bookshelf: nothing on To
// Read has ever been read, so ordering it by date read leaves it in no order
// at all, and ordering Read by date added is a list of when the reader found
// each book rather than of when they finished it. SHELF_DATE asks each shelf
// for the date it has - which is the only order under which every shelf on a
// display reads as most-recent-first at once.
//
// Did Not Finish goes with Read: a book you started and stopped has a date it
// was put down, and it comes back in the feed as a read date. A shelf this
// does not know about falls to the date every book has.
const SHELF_DATE_FIELD = {
  'to-read': 'dateAdded',
  'currently-reading': 'dateAdded',
  read: 'dateRead',
  'did-not-finish': 'dateRead',
};

// And falling back to the date added when it has no read date, which is not a
// tidy-up: docs/goodreads.md measured a read date on one book in sixteen,
// because Goodreads makes you set one by hand. Without the fallback a Read
// shelf under this order would be three books in date order followed by two
// hundred in alphabetical order, which reads as a fault rather than as an
// order. A date added is stamped on everything, so the fallback always has
// something to give.
//
// The cost is that two different meanings share one ordering - when it was
// finished, or failing that when it arrived - and that is the trade. A shelf
// that wants one meaning only can be sorted by the field itself.
//
// It is still possible for this to come back empty: a book with neither date.
// That one goes to the end, because missing is not the same as smallest.
export function shelfDate(book) {
  return book[SHELF_DATE_FIELD[book.shelf] || 'dateAdded'] || book.dateAdded || '';
}

// A leading article is not part of a title for filing purposes: The Hobbit
// goes under H. Only the first word goes, so "A Tale of Two Cities" keeps
// everything after "A".
const LEADING_ARTICLE = /^(?:a|an|the)\s+/i;

// Spaces, hyphens and apostrophes are skipped, which is what puts "Dont Look
// Back" next to "Don't Look Back" rather than a dozen entries away. Each is
// listed in the shapes titles really use: the curly apostrophe and the prime,
// the non-breaking space, the en, em and figure dashes.
const IGNORED_MARKS = /[\s\u00a0'\u2018\u2019\u02bc\u2032\-\u2010\u2011\u2012\u2013\u2014]/g;

// titleSortKey is the form a title is filed under: no leading article, none
// of the marks above, and lowercase so case never decides an order.
export function titleSortKey(title) {
  const trimmed = String(title || '').trim();
  if (!trimmed) return '';

  // Each step falls back to the one before it when stripping leaves nothing,
  // so a book actually called "The" files under "the" rather than under the
  // empty string, which the shelf would read as having no title at all.
  const filed = trimmed.replace(LEADING_ARTICLE, '').trim() || trimmed;
  const stripped = filed.replace(IGNORED_MARKS, '') || trimmed.replace(IGNORED_MARKS, '');
  return (stripped || trimmed).toLowerCase();
}

// Goodreads gives an author as they are printed on the cover, first name
// first, so alphabetizing by author means finding where the surname starts.

// Dropped from the end before looking, since none of them is the name.
const SUFFIXES = new Set(['jr', 'jr.', 'sr', 'sr.', 'ii', 'iii', 'iv', 'phd', 'ph.d.', 'md', 'm.d.']);

// Particles belong to the surname that follows them - Le Guin files under L,
// not G - and they are matched case-insensitively because a name may print
// them either way.
const PARTICLES = new Set([
  'de', 'del', 'della', 'der', 'den', 'di', 'du', 'da', 'dos', 'das',
  'la', 'le', 'van', 'von', 'ter', 'ten', 'op', 'af', 'al', 'bin', 'ibn', "'t",
]);

// authorSurname returns the part of a name a shelf files under. It is derived
// here rather than stored on the book, so it applies to everything already
// synced instead of waiting for the next one.
export function authorSurname(author) {
  // Collapsed, because the feed really does carry "John  Green" and
  // "Michael   Lewis" with the extra spaces in them.
  const parts = String(author || '').trim().split(/\s+/).filter(Boolean);
  while (parts.length > 1 && SUFFIXES.has(parts[parts.length - 1].toLowerCase())) {
    parts.pop();
  }
  if (parts.length === 0) return '';

  let start = parts.length - 1;
  while (start > 0 && PARTICLES.has(parts[start - 1].toLowerCase())) {
    start--;
  }
  return parts.slice(start).join(' ');
}

// Goodreads prints a series in the title, in a parenthesis at the end:
// "All Systems Red (The Murderbot Diaries, #1)". Only that shape counts as a
// series here. A bare number is not enough to go on - Fahrenheit 451 and 1984
// would both become book one of something - and neither is "(Book 2)", which
// the feed does not produce.
//
// The last such parenthesis wins, because a title can carry one of its own
// first: the feed really does hand back "We Are Legion (We Are Bob)
// (Bobiverse, #1)".
const SERIES_GROUP = /\(([^()]*#\s*\d+(?:\.\d+)?[^()]*)\)/g;

// The number itself, and whatever precedes it as the series name. A novella
// numbered #4.5 keeps its half, which is what files it between four and five,
// and an omnibus "#1-3" files under the first volume it contains.
const SERIES_NUMBER = /^(.*?)#\s*(\d+(?:\.\d+)?)/;

// seriesOf returns which series a title says it belongs to and where in it,
// or null for a title that says nothing. Like the surname, it is read off the
// title rather than stored on the book, so it covers everything already
// synced.
export function seriesOf(title) {
  let group = null;
  for (const match of String(title || '').matchAll(SERIES_GROUP)) group = match[1];
  if (group === null) return null;

  const parsed = SERIES_NUMBER.exec(group);
  if (!parsed) return null;

  // The name is normalised the way titles are, so "The Murderbot Diaries" and
  // "Murderbot Diaries" are one series rather than two. An unnamed one -
  // "All Systems Red (#1)" - keeps the empty name, which groups it with the
  // author's other unnamed entries and nothing else.
  return {
    name: titleSortKey(parsed[1].replace(/[,;]\s*$/, '')),
    number: Number(parsed[2]),
  };
}

// sortKey is the value a book is ordered by, in a form where ascending means
// what a reader expects: oldest first, lowest-rated first, A first.
function sortKey(book, order) {
  switch (order) {
    case TITLE:
      return titleSortKey(book.title);
    case AUTHOR:
      return authorSurname(book.author);
    case RATING:
      // The reader's own rating when they gave one, the Goodreads average
      // otherwise. Both run 0-5, and a book nobody has rated has neither.
      return book.userRating || book.rating || 0;
    case DATE_READ:
      return book.dateRead || '';
    case SHELF_DATE:
      return shelfDate(book);
    case PUBLISHED:
      // A year, not a date: it is all Goodreads gives, and a book with none
      // goes to the end the way an unrated one does.
      return book.publishedYear || 0;
    default:
      return book.dateAdded || '';
  }
}

// A book with nothing to sort on goes to the end whichever way the shelf is
// pointing. Missing is not the same as smallest: most of a To Read shelf has
// no read date, and reversing the shelf should not bring all of it to the
// front.
const missing = (value) => value === '' || value === 0;

// groupSeries files each entry under the title of the first book of its
// series, so a series moves through an author's shelf as one block instead of
// scattering alphabetically. Entries that are not in a series stay filed under
// their own title, which is what the shelf did before any of this.
//
// The block is keyed by author as well as by series name, because two writers
// can number a "Book One" and only one author's books are ever adjacent.
function groupSeries(keyed) {
  const firsts = new Map();
  for (const entry of keyed) {
    if (!entry.series) continue;
    entry.seriesKey = `${entry.value}\u0000${entry.series.name}`;
    const first = firsts.get(entry.seriesKey);
    // Ties on the number - a duplicate, or two omnibuses opening at the same
    // volume - fall to the title, so which book anchors the block does not
    // depend on the order the sync happened to hand them back in.
    if (!first
      || entry.series.number < first.series.number
      || (entry.series.number === first.series.number && entry.title < first.title)) {
      firsts.set(entry.seriesKey, entry);
    }
  }
  for (const entry of keyed) {
    if (entry.series) entry.group = firsts.get(entry.seriesKey).title;
  }
}

export function sortBooks(books, order, direction) {
  const descending = direction === DESCENDING;

  // Keys worked out once per book rather than once per comparison, since both
  // of them strip and lowercase their way through a string.
  const keyed = books.map((book) => {
    const title = titleSortKey(book.title);
    return {
      book,
      value: sortKey(book, order),
      title,
      // Where the book files when the shelf breaks a tie on the sort key.
      // groupSeries refiles a series under its first book; everything else
      // stays under its own title.
      group: title,
      // Only an author sort keeps a series together. Under a title sort the
      // title is the thing being ordered by, so moving a book off its own
      // letter would be wrong; under a date or rating sort a series is not
      // adjacent to begin with.
      series: order === AUTHOR ? seriesOf(book.title) : null,
    };
  });
  groupSeries(keyed);

  keyed.sort((left, right) => {
    const a = left.value;
    const b = right.value;

    if (missing(a) !== missing(b)) return missing(a) ? 1 : -1;
    if (!missing(a)) {
      const compared = typeof a === 'number' ? a - b : a.localeCompare(b);
      if (compared !== 0) return descending ? -compared : compared;
    }

    // Same key, or neither has one. For an author sort this is the rule
    // rather than a fallback: it is what puts one writer's books in order
    // underneath them. It stays ascending even on a descending shelf, so
    // reversing the shelf does not also reverse each author against himself.
    // Falling back to the title rather than to input order also keeps a shelf
    // from reshuffling every time a sync hands the books back differently.
    //
    // The title compared is the first book of the series for anything in one,
    // so the whole series lands where its first book would have, in one piece.
    const compared = left.group.localeCompare(right.group);
    if (compared !== 0) return compared;

    // Inside the block, reading order - and ascending whichever way the shelf
    // points, for the same reason the author's own books are.
    if (left.seriesKey && left.seriesKey === right.seriesKey
      && left.series.number !== right.series.number) {
      return left.series.number - right.series.number;
    }

    return left.title.localeCompare(right.title);
  });

  return keyed.map((entry) => entry.book);
}
